import type { FC, ReactElement } from 'react';
import { useEffect, useRef, useMemo } from 'react';

import { HelpText } from '../../../services/helpTranslations/reactBindings/HelpText';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import type { HelpAccordionProps } from '../types';

import { HelpChapter } from './HelpChapter';

import styles from './HelpAccordion.module.css';

export const HelpAccordion: FC<HelpAccordionProps> = ({ helpConfig, isRTL, chaptersToHide }): ReactElement => {
  const mods = useModifiers(styles, 'help', { rtl: isRTL }, { withBaseClass: true });
  const chapterRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const defaultOpenChapters = useMemo(() => helpConfig.defaultOpenChapters ?? [], [helpConfig.defaultOpenChapters]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (defaultOpenChapters.length > 0) {
      const firstChapter = String(defaultOpenChapters[0]);
      const targetChapter = chapterRefs.current[firstChapter];

      if (targetChapter) {
        // Use a small delay to ensure the accordion is fully opened
        timeoutId = setTimeout(() => {
          targetChapter.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    }

    return (): void => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [defaultOpenChapters]);

  return (
    <div className={mods}>
      {Object.entries(helpConfig.chapters).map(([chapterName, Sections]) => {
        if (chaptersToHide?.includes(chapterName)) {
          return null;
        }
        return (
          <div
            key={chapterName}
            ref={(el) => {
              chapterRefs.current[chapterName] = el;
            }}
          >
            <HelpChapter
              chapterName={chapterName}
              isOpen={defaultOpenChapters.includes(chapterName)}
              title={<HelpText tk={`${chapterName}.title`} />}
            >
              <Sections />
            </HelpChapter>
          </div>
        );
      })}
    </div>
  );
};
